import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ResponseError, formatOTPWait, otpRetryAt, otpWaitSeconds } from "@norma-bi/bi-api";
import { useRootAPI, useUnauthenticatedAPI } from "@/api";
import { useAuth } from "@/Auth";
import { useAccountNumberInput } from "@/hooks/useAccountNumberInput";
import { useEmailInput } from "@/hooks/useEmailInput";
import { customToast } from "@/utils/customToast";

type AddBusinessDialogProps = {
  onClose: () => void;
};

export function AddBusinessDialog({ onClose }: AddBusinessDialogProps) {
  const auth = useAuth();
  const loginAPI = useUnauthenticatedAPI();
  const rootAPI = useRootAPI();
  const queryClient = useQueryClient();
  const { accountNumber, handleAccountNumberChange, isAccountNumberValid } =
    useAccountNumberInput();
  const { email, handleEmailChange, isEmailValid } = useEmailInput(
    auth.isAuthenticated ? (auth.user.identities[0]?.email ?? "") : "",
  );
  const [challengeID, setChallengeID] = React.useState<string>();
  const [code, setCode] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string>();
  const [requestRetryAt, setRequestRetryAt] = React.useState<Date>();
  const [verifyRetryAt, setVerifyRetryAt] = React.useState<Date>();
  const [now, setNow] = React.useState(Date.now());
  React.useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const requestWait = otpWaitSeconds(requestRetryAt, now);
  const verifyWait = otpWaitSeconds(verifyRetryAt, now);

  async function submit(requestNewCode = false) {
    setBusy(true);
    setError(undefined);
    try {
      if (requestNewCode || !challengeID) {
        const result = await loginAPI.postLoginOtpChallenge({
          RequestOTPChallengeRequest: { business_ref: Number(accountNumber), email },
        });
        if (result.status === "code_sent" && result.challenge_id) {
          setChallengeID(result.challenge_id);
          setCode("");
          setRequestRetryAt(result.retry_at);
          setVerifyRetryAt(undefined);
        } else if (result.status === "business_not_found") {
          setError("We could not find this Foodics business reference.");
        } else if (result.status === "user_not_found") {
          setError("This email does not belong to a user of that Foodics business.");
        } else {
          setError("A code could not be sent. Please try again shortly.");
        }
        return;
      }

      const result = await rootAPI.postOrganizationBusinessesOtpVerify({
        AddBusinessOTPRequest: { challenge_id: challengeID, code },
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["businesses"] }),
        queryClient.invalidateQueries({ queryKey: ["self"] }),
      ]);
      customToast(
        result.status === "installing"
          ? "Business added. Installation is in progress."
          : result.status === "moved"
            ? "Business moved into this organization."
            : "Business is already in this organization.",
        "success",
        "bottom",
      );
      onClose();
    } catch (caught) {
      const retryAt = await otpRetryAt(caught);
      if (retryAt && (requestNewCode || !challengeID)) setRequestRetryAt(retryAt);
      if (caught instanceof ResponseError) {
        if (caught.response.status === 400) {
          setError("Check the code and try again. If it expired, request a new code.");
        } else if (caught.response.status === 403) {
          setError("An admin of the other business must verify the code to add it here.");
        } else if (caught.response.status === 409) {
          setError(
            "This code has been used, or the business is still installing. Request a new code if needed, or try the move after installation finishes.",
          );
        } else if (caught.response.status === 429) {
          if (challengeID && !requestNewCode) setVerifyRetryAt(retryAt);
          setError("Please wait until the countdown ends before trying again.");
        } else {
          setError(
            challengeID
              ? "An unexpected error stopped us from adding or moving this business. The code is still usable until it expires; please try again."
              : "An unexpected error stopped us from sending a code. Please try again.",
          );
        }
      } else {
        setError(
          challengeID
            ? "We could not confirm whether the business was added or moved. Refresh your businesses before trying again."
            : "We could not confirm the code request. Check your email before trying again.",
        );
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) onClose();
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-business-title"
        className="w-full max-w-md rounded-xl bg-pureWhite p-6 shadow-xl text-dark"
      >
        <h2 id="add-business-title" className="mb-3 text-lg font-medium">
          Add a Foodics business
        </h2>
        <p className="mb-5 text-sm text-feintGray">
          Enter a business reference and your email in that Foodics business. New installations
          require Foodics app-management permission. Moving an installed business requires BI admin
          access in its current organization.
        </p>
        {error && (
          <div
            role="alert"
            className="mb-4 rounded-lg border border-sematicRed bg-sematicRed/10 p-3 text-sm text-errorContent"
          >
            {error}
          </div>
        )}
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
          className="space-y-4"
        >
          <label className="block text-sm">
            Business Reference (F5 Number)
            <input
              className="thoughtspot-login-input mt-2 w-full"
              value={accountNumber}
              onChange={handleAccountNumberChange}
              inputMode="numeric"
              disabled={Boolean(challengeID) || busy}
              required
            />
          </label>
          <label className="block text-sm">
            Your Foodics email
            <input
              className="thoughtspot-login-input mt-2 w-full"
              type="email"
              value={email}
              onChange={handleEmailChange}
              disabled={Boolean(challengeID) || busy}
              required
            />
          </label>
          {challengeID && (
            <label className="block text-sm">
              Six-digit code
              <input
                className="thoughtspot-login-input mt-2 w-full"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))}
                maxLength={6}
                autoFocus
                required
              />
            </label>
          )}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              className="flex-1 rounded-lg border border-lightGray px-4 py-2"
              onClick={onClose}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 rounded-lg bg-primaryColor px-4 py-2 text-white disabled:opacity-50"
              disabled={
                busy ||
                !isAccountNumberValid ||
                Number(accountNumber) <= 0 ||
                !isEmailValid ||
                Boolean(challengeID && (code.length !== 6 || verifyWait > 0)) ||
                Boolean(!challengeID && requestWait > 0)
              }
            >
              {busy
                ? "Please wait..."
                : challengeID
                  ? verifyWait > 0
                    ? `Try again in ${formatOTPWait(verifyWait)}`
                    : "Add business"
                  : requestWait > 0
                    ? `Send code in ${formatOTPWait(requestWait)}`
                    : "Send code"}
            </button>
          </div>
          {challengeID && (
            <button
              type="button"
              className="w-full text-sm underline"
              disabled={busy || requestWait > 0}
              onClick={() => {
                void submit(true);
              }}
            >
              {requestWait > 0
                ? `Request a new code in ${formatOTPWait(requestWait)}`
                : "Request a new code"}
            </button>
          )}
        </form>
      </section>
    </div>
  );
}
